import { Suspense, useEffect, type ReactNode } from 'react'
import { Routes, Route, Navigate, Outlet, useParams, useLocation } from 'react-router-dom'
import { Layout } from './components/Layout'
import { ProtectedRoute } from './components/ProtectedRoute'
import { RouteErrorBoundary } from './components/ErrorBoundary'
import { useAuthStore } from './stores/authStore'
import { lazyWithRetry } from './lib/lazyWithRetry'
import { logger } from './lib/logger'
import { useOnline } from './hooks/useOnline'
import { PageSkeleton } from './components/ui/Skeletons'
import { WifiOff } from 'lucide-react'
import { usePermission } from './hooks/usePermission'
import type { CapabilityName } from './lib/permissions'
import { ForbiddenState } from './components/ui/QueryState'
import { PageHeader } from './components/ui/PageHeader'
import { isLocalFixturePath } from './lib/localFixtures'

// Lazy-loaded pages — each page is code-split into its own chunk. A chunk that
// 404s after a deploy triggers one automatic reload (see lazyWithRetry).
const PaymentsListVault = lazyWithRetry(() => import('./pages/PaymentsListVault'))
const HubPaymentsVault = lazyWithRetry(() => import('./pages/HubPaymentsVault'))
const PunchListVault = lazyWithRetry(() => import('./pages/PunchListVault'))
const GrowthDesignPreview = lazyWithRetry(() => import('./pages/GrowthDesignPreview'))
const MaterialsVault = lazyWithRetry(() => import('./pages/MaterialsVault'))
const MailVault = lazyWithRetry(() => import('./pages/MailVault'))
const Login = lazyWithRetry(() => import('./pages/Login'))
const ForgotPassword = lazyWithRetry(() => import('./pages/ForgotPassword'))
const ResetPassword = lazyWithRetry(() => import('./pages/ResetPassword'))
const EmailChangeLink = lazyWithRetry(() => import('./pages/EmailChangeLink'))
const ChangePassword = lazyWithRetry(() => import('./pages/ChangePassword'))
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'))
const Customers = lazyWithRetry(() => import('./pages/Customers'))
const QuoteBuilder = lazyWithRetry(() => import('./pages/Quotes/QuoteBuilder'))
const Calendar = lazyWithRetry(() => import('./pages/Calendar'))
const Inbox = lazyWithRetry(() => import('./pages/Inbox'))
const Notifications = lazyWithRetry(() => import('./pages/Notifications'))
const Workflows = lazyWithRetry(() => import('./pages/Workflows'))
const Marketing = lazyWithRetry(() => import('./pages/Marketing'))
const Reports = lazyWithRetry(() => import('./pages/Reports'))
const MarginReport = lazyWithRetry(() => import('./pages/MarginReport'))
const Settings = lazyWithRetry(() => import('./pages/Settings'))
const InspectionsVault = lazyWithRetry(() => import('./pages/InspectionsVault'))
const ReviewAskVault = lazyWithRetry(() => import('./pages/ReviewAskVault'))
const AdsVault = lazyWithRetry(() => import('./pages/AdsVault'))
const Work = lazyWithRetry(() => import('./pages/Work'))
const CrewOffline = lazyWithRetry(() => import('./pages/CrewOffline'))
const CrewDay = lazyWithRetry(() => import('./pages/CrewDay'))
const CrewDayVault = lazyWithRetry(() => import('./pages/CrewDayVault'))
const CrewFieldVault = lazyWithRetry(() => import('./pages/CrewFieldVault'))
const Purchasing = lazyWithRetry(() => import('./pages/Purchasing'))
const PublicBooking = lazyWithRetry(() => import('./pages/PublicBooking'))
const PublicBookingManage = lazyWithRetry(() => import('./pages/PublicBookingManage'))
const ClientHub = lazyWithRetry(() => import('./pages/ClientHub'))
const PublicSmsTerms = lazyWithRetry(() => import('./pages/PublicSmsTerms'))
const PublicLeadForm = lazyWithRetry(() => import('./pages/PublicLeadForm'))
const PublicJobPayment = lazyWithRetry(() => import('./pages/PublicJobPayment'))
const CrewSnapshotDay = lazyWithRetry(() => import('./pages/CrewSnapshotDay'))
const PublicUnsubscribe = lazyWithRetry(() => import('./pages/PublicUnsubscribe'))
const LegalPrivacy = lazyWithRetry(() => import('./pages/LegalPrivacy'))
const NotFound = lazyWithRetry(() => import('./pages/NotFound'))
// Payments pull in the QR encoder and card flow; most sessions never open them.
const PaymentSheet = lazyWithRetry(() => import('@/components/Payments/PaymentSheet').then((m) => ({ default: m.PaymentSheet })))
const PaymentsList = lazyWithRetry(() => import('@/components/Payments/PaymentsList').then((m) => ({ default: m.PaymentsList })))
const PaymentTools = lazyWithRetry(() => import('@/components/Payments/PaymentTools').then((m) => ({ default: m.PaymentTools })))

// Redirect helper for entity deep links — detects entity type from path and adds prefix
function RedirectToWork() {
  const { id } = useParams()
  const location = useLocation()
  let prefix = ''
  if (location.pathname.startsWith('/quotes/')) prefix = 'q:'
  else if (location.pathname.startsWith('/jobs/')) prefix = 'job:'
  else if (location.pathname.startsWith('/invoices/')) prefix = 'inv:'
  else if (location.pathname.startsWith('/deals/')) prefix = 'deal:'
  return <Navigate to={`/work/${encodeURIComponent(prefix + id)}`} replace />
}

function RedirectToCustomersPerson() {
  const { id } = useParams()
  return <Navigate to={`/customers/people/${id}`} replace />
}

function RedirectToCustomersCompany() {
  const { id } = useParams()
  return <Navigate to={`/customers/companies/${id}`} replace />
}

/** Pins a subtree to the default tokens; display: contents keeps it out of layout. */
function ThemePin() {
  return <div className="theme-pin" style={{ display: 'contents' }}><Outlet /></div>
}

/**
 * A screen this role can't use (UX-010): the shared "You don't have access"
 * card instead of a live form whose every request fails. Crew reach Take
 * payment, Billing tools and the quote builder only by typing the URL or an
 * old link; the entry points are already hidden.
 */
export function RequirePermission({ capability, title, what, children }: {
  capability: CapabilityName
  /** Page title shown above the card. */
  title: string
  /** "take payments" → "Your role can't take payments." */
  what: string
  children: ReactNode
}) {
  const allowed = usePermission(capability)
  if (allowed) return <>{children}</>
  return (
    <div className="page-container animate-fade-up">
      <PageHeader title={title} />
      <ForbiddenState message={`Your role can't ${what}. Ask an admin if you need access.`} />
    </div>
  )
}

/**
 * Route-level Suspense fallback. While a page chunk downloads it shows a
 * layout-shaped skeleton (UX-096). If the device is offline and the chunk was
 * never downloaded, it says so in-app — lazyWithRetry keeps the import pending
 * and retries when the connection returns, instead of reloading into the
 * browser's "No internet" page (UX-052).
 */
export function RouteFallback() {
  const online = useOnline()
  if (!online) {
    return (
      <div className="page-container">
        <div role="status" className="ds-tile mx-auto mt-8 max-w-md p-8 text-center" data-testid="route-offline">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/[0.06]">
            <WifiOff className="h-5 w-5" style={{ color: 'var(--midnight-blush)' }} aria-hidden="true" />
          </div>
          <h2 className="text-base font-semibold text-white">You're offline</h2>
          <p className="mx-auto mt-2 max-w-sm text-sm" style={{ color: 'var(--midnight-text2)' }}>
            This page hasn't been downloaded to this device yet. It will open by itself when you're back online.
          </p>
        </div>
      </div>
    )
  }
  return <PageSkeleton />
}

/**
 * Download the screens a crew lead needs (Home, Work + the job drawer,
 * Calendar, Inbox) right after sign-in, while there's signal — so tapping them
 * later on a dead connection still works (UX-052). Runs when the browser is idle.
 */
const CREW_CRITICAL = [Dashboard, Work, Calendar, Inbox, Customers]
function PrefetchCrewRoutes() {
  useEffect(() => {
    type IdleWindow = Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }
    const w = window as IdleWindow
    const run = () => CREW_CRITICAL.forEach((c) => { void c.preload() })
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(run, { timeout: 4000 })
      return () => (window as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback?.(id)
    }
    const t = window.setTimeout(run, 2500)
    return () => window.clearTimeout(t)
  }, [])
  return null
}

/**
 * Persistent shell for every authenticated route.
 *
 * This is a react-router *layout route*: <Layout> (sidebar, topbar and the
 * Softphone with its Twilio Device + live call) is rendered once and the
 * matched child route renders into <Outlet/>. Previously each route wrapped
 * its own <Layout>, so any hop through a route without one (the redirect-only
 * routes such as /quotes/:id -> /work/q:id) unmounted the shell and with it
 * the Softphone, whose cleanup hangs up the active call.
 *
 * Every redirect-only route lives under this layout route too, so a redirect
 * never leaves the shell. Suspense and the error boundary sit *inside* the
 * layout so a slow chunk or a crashing page never takes the shell down.
 */
export function AuthedLayoutRoute() {
  return (
    <ProtectedRoute>
      <Layout>
        <PrefetchCrewRoutes />
        <RouteErrorBoundary fullScreen={false}>
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </RouteErrorBoundary>
      </Layout>
    </ProtectedRoute>
  )
}

export function AppRoutes() {
  const restoreSession = useAuthStore((s) => s.restoreSession)
  const isFixture = isLocalFixturePath(useLocation().pathname)

  useEffect(() => {
    if (isFixture) return
    restoreSession().catch((err) => {
      // restoreSession only re-throws on network errors; don't force logout on a blip
      logger.warn('[App] Session restore network error — keeping current auth state:', err)
    })
  }, [restoreSession, isFixture])

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/crew-offline" element={<CrewOffline />} />
        <Route path="/vault/crew-day" element={<CrewDayVault />} />
        <Route path="/vault/crew-field" element={<CrewFieldVault />} />
        <Route path="/vault/payments-list" element={<PaymentsListVault />} />
        <Route path="/vault/hub-payments" element={<HubPaymentsVault />} />
        <Route path="/vault/punch-list" element={<PunchListVault />} />
        <Route path="/vault/growth" element={<GrowthDesignPreview />} />
        <Route path="/__design/growth" element={<GrowthDesignPreview />} />
        <Route path="/vault/inspections" element={<InspectionsVault />} />
        <Route path="/vault/review-ask" element={<ReviewAskVault />} />
        <Route path="/vault/ads" element={<AdsVault />} />
        <Route path="/vault/materials-log" element={<MaterialsVault />} />
        <Route path="/vault/mail" element={<MailVault />} />
        {/* Public auth routes — no sidebar/topbar */}
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* Emailed by an email change (routes/users.ts): the new inbox confirms, the old one can undo. */}
        <Route path="/verify-email-change" element={<EmailChangeLink mode="confirm" />} />
        <Route path="/revert-email-change" element={<EmailChangeLink mode="revert" />} />
        {/* Public on purpose: ProtectedRoute redirects here when mustResetPassword
            is set, so wrapping it would loop. The page guards itself (signed-out
            -> /login, no forced reset -> /settings/security). */}
        <Route path="/change-password" element={<ChangePassword />} />
        {/* Customer-facing pages carry the tenant's look, not the user's
            theme: ThemePin hands them the brand look, Dark iOS Neon
            (DECISIONS §26). */}
        <Route element={<ThemePin />}>
          {/* Tier 2 Wave 3A — public booking page (no auth) */}
          {/* Marketing — hosted/embedded lead form and the campaign unsubscribe page (no auth). */}
          <Route path="/f/:formId" element={<PublicLeadForm />} />
          <Route path="/pay" element={<PublicJobPayment />} />
          <Route path="/unsubscribe" element={<PublicUnsubscribe />} />
          {/* HUB-01: customer client hub (magic link, no staff login). */}
          <Route path="/hub/:token" element={<ClientHub />} />
          <Route path="/book/:workspaceSlug" element={<PublicBooking />} />
          {/* Tier 2 Wave 3C — customer cancel + reschedule pages. Order matters:
              literal /cancel and /reschedule must match before the catch-all
              :serviceSlug below. */}
          <Route path="/book/:workspaceSlug/cancel" element={<PublicBookingManage action="cancel" />} />
          <Route path="/book/:workspaceSlug/reschedule" element={<PublicBookingManage action="reschedule" />} />
          {/* A2P 10DLC — messaging disclosure submitted to TCR as opt-in evidence.
              Literal segment, so it must precede :serviceSlug for the same reason
              /cancel and /reschedule do. */}
          <Route path="/book/:workspaceSlug/sms-terms" element={<PublicSmsTerms />} />
          {/* The business's own privacy policy (names it, shows its contact block). */}
          <Route path="/book/:workspaceSlug/privacy" element={<LegalPrivacy />} />
          <Route path="/book/:workspaceSlug/:serviceSlug" element={<PublicBooking />} />
          {/* Public privacy policy — the campaign registration links here. */}
          <Route path="/legal/privacy" element={<LegalPrivacy />} />
          {/* Wave 3B fallback URLs (still in customer inboxes). */}
          <Route path="/booking/cancel" element={<PublicBookingManage action="cancel" />} />
          <Route path="/booking/reschedule" element={<PublicBookingManage action="reschedule" />} />
        </Route>

        {/* Authenticated app — one persistent layout route (sidebar/topbar/softphone) */}
        <Route element={<AuthedLayoutRoute />}>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard"    element={<Dashboard />} />
          <Route path="/customers/*"  element={<Customers />} />
          <Route path="/contacts/:id" element={<RedirectToCustomersPerson />} />
          <Route path="/contacts"     element={<Navigate to="/customers/people" replace />} />
          <Route path="/companies/:id" element={<RedirectToCustomersCompany />} />
          <Route path="/companies"    element={<Navigate to="/customers/companies" replace />} />
          <Route path="/work/:id?"    element={<Work />} />
          <Route path="/purchasing" element={<RequirePermission capability="viewCosting" title="Purchasing" what="see purchasing"><Purchasing /></RequirePermission>} />
          <Route path="/quotes/builder" element={<RequirePermission capability="createQuote" title="New quote" what="create quotes"><QuoteBuilder /></RequirePermission>} />
          <Route path="/quotes/new"   element={<Navigate to="/quotes/builder" replace />} />
          <Route path="/quotes/:id"   element={<RedirectToWork />} />
          <Route path="/quotes"       element={<Navigate to="/work" replace />} />
          <Route path="/jobs/new"     element={<Navigate to="/work" replace />} />
          <Route path="/jobs/:id"     element={<RedirectToWork />} />
          <Route path="/jobs"         element={<Navigate to="/work" replace />} />
          <Route path="/deals/:id"    element={<RedirectToWork />} />
          <Route path="/deals"        element={<Navigate to="/work" replace />} />
          <Route path="/pipeline"     element={<Navigate to="/work" replace />} />
          <Route path="/pipeline/:id" element={<Navigate to="/work" replace />} />
          <Route path="/calendar"     element={<Calendar />} />
          <Route path="/crew-day" element={<CrewDay />} />
          <Route path="/crew-assigned" element={<CrewSnapshotDay />} />
          <Route path="/invoices/:id" element={<RedirectToWork />} />
          <Route path="/payments"     element={<RequirePermission capability="recordPayment" title="Take payment" what="take payments"><PaymentSheet /></RequirePermission>} />
          <Route path="/payments/all" element={<RequirePermission capability="viewPayments" title="Payments" what="see payments"><PaymentsList /></RequirePermission>} />
          <Route path="/payments/tools" element={<RequirePermission capability="recordPayment" title="Billing tools" what="use billing tools"><PaymentTools /></RequirePermission>} />
          <Route path="/invoices"     element={<Navigate to="/work" replace />} />
          <Route path="/inbox"        element={<Inbox />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/workflows/*"  element={<Workflows />} />
          <Route path="/marketing/*"  element={<Marketing />} />
          <Route path="/social-media" element={<Navigate to="/marketing/social" replace />} />
          <Route path="/reviews"      element={<Navigate to="/marketing/reviews" replace />} />
          <Route path="/reports"      element={<Reports />} />
          <Route path="/reports/margin" element={<RequirePermission capability="viewCosting" title="Margin by trade" what="see job margins"><MarginReport /></RequirePermission>} />
          <Route path="/ads"          element={<Navigate to="/reports?tab=spend" replace />} />
          <Route path="/settings/*"   element={<Settings />} />
          {/* Unknown URLs render inside the app frame, so a signed-in person
              keeps the sidebar / dock instead of a bare black page (UX-208).
              Signed-out visitors go to sign-in like any other app URL. */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  )
}
